import {
  Drawer,
  Stack,
  Group,
  Text,
  Badge,
  Table,
  Accordion,
  Code,
  ScrollArea,
  Skeleton,
  Alert,
  Tooltip,
  Card,
} from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import { getWafRuleDetail, getWafRuleLogs } from '@/api/waf'
import type { WafRuleKind } from '@/api/waf'

// ─── Mapa de labels para tipos de regra ──────────────────────────────────────

const RULE_KIND_LABEL: Record<WafRuleKind, string> = {
  managed: 'Managed group',
  rate:    'Rate-based',
  ip:      'IP set',
  geo:     'Geo',
  byte:    'String match',
  regex:   'Regex',
  json:    'JSON bruto',
  allow:   'Allow',
  other:   'Outro',
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface WafRuleDetailDrawerProps {
  wafId: number
  ruleName: string | null
  opened: boolean
  onClose: () => void
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatTimestamp(ts?: string): string {
  if (!ts) return '—'
  try {
    return new Date(ts).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  } catch {
    return ts
  }
}

// ─── Componente ───────────────────────────────────────────────────────────────

export function WafRuleDetailDrawer({ wafId, ruleName, opened, onClose }: WafRuleDetailDrawerProps) {
  const detailQuery = useQuery({
    queryKey: ['waf-rule-detail', wafId, ruleName],
    queryFn: () => getWafRuleDetail(wafId, ruleName!),
    enabled: opened && !!ruleName,
  })

  const logsQuery = useQuery({
    queryKey: ['waf-rule-logs', wafId, ruleName],
    queryFn: () => getWafRuleLogs(wafId, ruleName!, 7),
    enabled: opened && !!ruleName,
  })

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="xl"
      title={
        <Text ff="monospace" fw={700} size="sm">
          {ruleName ?? '—'}
        </Text>
      }
      styles={{ body: { padding: '1rem' } }}
    >
      {detailQuery.isLoading && (
        <Stack gap="sm">
          <Skeleton height={24} />
          <Skeleton height={24} />
          <Skeleton height={24} />
          <Skeleton height={100} />
        </Stack>
      )}

      {detailQuery.isError && (
        <Alert color="red">
          Não foi possível carregar os detalhes desta regra. Tente novamente.
        </Alert>
      )}

      {detailQuery.data && (() => {
        const { rule, sampled, sub_rule_breakdown, sample_error } = detailQuery.data

        return (
          <Stack gap="xl">
            {/* ── Seção 0: O que esta regra faz ─────────────────────────── */}
            {(detailQuery.data.rule?.description || detailQuery.data.description_aws) && (
              <Card withBorder padding="sm" radius="md">
                <Text fw={600} size="sm" mb={4}>O que esta regra faz</Text>
                {detailQuery.data.rule?.description && (
                  <Text size="sm">{detailQuery.data.rule.description}</Text>
                )}
                {detailQuery.data.description_aws && (
                  <Text size="xs" c="dimmed" mt={6}>
                    Descrição oficial AWS (em inglês): {detailQuery.data.description_aws}
                  </Text>
                )}
              </Card>
            )}

            {/* ── Seção 1: Configuração ──────────────────────────────────── */}
            <Stack gap="xs">
              <Text fw={700} size="sm">Configuração</Text>

              <Group gap="xs" wrap="wrap">
                <Text size="sm" c="dimmed">Tipo:</Text>
                <Badge size="xs" variant="outline" color="gray">
                  {RULE_KIND_LABEL[rule.kind] ?? rule.kind}
                </Badge>

                <Text size="sm" c="dimmed" ml="sm">Ação efetiva:</Text>
                <Badge
                  size="xs"
                  variant="light"
                  color={rule.effective_action === 'active' ? 'red' : 'gray'}
                >
                  {rule.effective_action === 'active' ? 'Ativo' : 'Count'}
                </Badge>

                <Text size="sm" c="dimmed" ml="sm">Prioridade:</Text>
                <Text size="sm">{rule.priority}</Text>
              </Group>

              {rule.metric && (
                <Group gap="xs">
                  <Text size="sm" c="dimmed">Métrica:</Text>
                  <Text size="sm" ff="monospace">{rule.metric}</Text>
                </Group>
              )}

              {rule.host_scope && (
                <Group gap="xs">
                  <Text size="sm" c="dimmed">Aplicada ao domínio:</Text>
                  <Text size="sm" ff="monospace">{rule.host_scope}</Text>
                </Group>
              )}

              {rule.managed && (
                <Group gap="xs" wrap="wrap">
                  <Text size="sm" c="dimmed">Grupo gerenciado:</Text>
                  {rule.managed.vendor && (
                    <Badge size="xs" variant="outline" color="blue">{rule.managed.vendor}</Badge>
                  )}
                  {rule.managed.name && (
                    <Text size="sm" ff="monospace">{rule.managed.name}</Text>
                  )}
                  {rule.managed.version && (
                    <Text size="xs" c="dimmed">v{rule.managed.version}</Text>
                  )}
                </Group>
              )}

              <Accordion variant="contained" mt="xs">
                <Accordion.Item value="statement">
                  <Accordion.Control>
                    <Text size="sm">Statement JSON (configuração completa da regra)</Text>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <ScrollArea h={300}>
                      <Code block style={{ fontSize: 12 }}>
                        {JSON.stringify(rule.statement, null, 2)}
                      </Code>
                    </ScrollArea>
                  </Accordion.Panel>
                </Accordion.Item>
              </Accordion>
            </Stack>

            {/* ── Seção 2: Quebra por sub-regra ─────────────────────────── */}
            <Stack gap="xs">
              <Group gap="xs" align="baseline">
                <Text fw={700} size="sm">Quebra por sub-regra</Text>
                <Text size="xs" c="dimmed">(amostra recente ~3h)</Text>
              </Group>

              {sample_error ? (
                <Alert color="yellow">
                  Não foi possível obter a amostra recente.
                </Alert>
              ) : sub_rule_breakdown.length === 0 ? (
                <Text size="sm" c="dimmed">
                  Sem requisições casadas na amostra recente (~3h).
                </Text>
              ) : (
                <>
                  <Table striped withTableBorder style={{ maxWidth: 420 }}>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Sub-regra</Table.Th>
                        <Table.Th style={{ width: 80 }}>Contagem</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {sub_rule_breakdown.map((item) => (
                        <Table.Tr key={item.sub_rule}>
                          <Table.Td>
                            <Text size="xs" ff="monospace">{item.sub_rule}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Badge size="xs" variant="light" color="orange">
                              {item.count.toLocaleString('pt-BR')}
                            </Badge>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                  <Text size="xs" c="dimmed">
                    Amostra recente (~3h, até 500 requisições).
                  </Text>
                </>
              )}
            </Stack>

            {/* ── Seção 3: Requisições amostradas ───────────────────────── */}
            <Stack gap="xs">
              <Text fw={700} size="sm">Requisições amostradas</Text>

              {sampled.length === 0 ? (
                <Text size="sm" c="dimmed">Sem amostras.</Text>
              ) : (
                <ScrollArea>
                  <Table striped withTableBorder style={{ minWidth: 700 }}>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th style={{ width: 140 }}>Hora</Table.Th>
                        <Table.Th style={{ width: 120 }}>IP</Table.Th>
                        <Table.Th style={{ width: 60 }}>País</Table.Th>
                        <Table.Th style={{ width: 70 }}>Método</Table.Th>
                        <Table.Th>URI</Table.Th>
                        <Table.Th style={{ width: 70 }}>Ação</Table.Th>
                        <Table.Th>Sub-regra</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {sampled.map((s, i) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <Table.Tr key={i}>
                          <Table.Td>
                            <Text size="xs" ff="monospace">{formatTimestamp(s.timestamp)}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="xs" ff="monospace">{s.client_ip ?? '—'}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="xs">{s.country ?? '—'}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="xs" ff="monospace">{s.method ?? '—'}</Text>
                          </Table.Td>
                          <Table.Td style={{ maxWidth: 200 }}>
                            <Tooltip label={s.uri ?? '—'} withinPortal multiline w={320}>
                              <Text
                                size="xs"
                                ff="monospace"
                                style={{
                                  maxWidth: 200,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {s.uri ?? '—'}
                              </Text>
                            </Tooltip>
                          </Table.Td>
                          <Table.Td>
                            <Text size="xs">{s.action ?? '—'}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="xs" ff="monospace" c="dimmed">{s.rule ?? '—'}</Text>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </ScrollArea>
              )}
            </Stack>

            {/* ── Seção 4: Análise dos últimos 7 dias (logs) ────────────── */}
            <Stack gap="xs">
              <Text fw={700} size="sm">Análise dos últimos 7 dias (logs)</Text>

              {logsQuery.isLoading && (
                <Stack gap="xs">
                  <Skeleton height={20} />
                  <Skeleton height={20} />
                  <Skeleton height={20} />
                </Stack>
              )}

              {!logsQuery.isLoading && logsQuery.data && (() => {
                const logs = logsQuery.data

                if (!logs.enabled) {
                  return (
                    <Alert color="gray">
                      Logging do CloudWatch não está habilitado neste WAF — análise de 7 dias indisponível.
                    </Alert>
                  )
                }

                if (logs.error) {
                  return (
                    <Alert color="yellow">
                      Não foi possível consultar os logs agora.
                    </Alert>
                  )
                }

                return (
                  <Stack gap="md">
                    <Text size="sm">
                      Total de casamentos (7d):{' '}
                      <Text component="span" fw={700}>
                        {(logs.total ?? 0).toLocaleString('pt-BR')}
                      </Text>
                    </Text>

                    {/* Top URIs */}
                    <Stack gap={4}>
                      <Text size="sm" fw={600}>Top URIs</Text>
                      {!logs.top_uris || logs.top_uris.length === 0 ? (
                        <Text size="sm" c="dimmed">—</Text>
                      ) : (
                        <Table striped withTableBorder style={{ maxWidth: 500 }}>
                          <Table.Thead>
                            <Table.Tr>
                              <Table.Th>URI</Table.Th>
                              <Table.Th style={{ width: 80 }}>Hits</Table.Th>
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            {logs.top_uris.map((item, i) => (
                              // eslint-disable-next-line react/no-array-index-key
                              <Table.Tr key={i}>
                                <Table.Td>
                                  <Text size="xs" ff="monospace">{item.key ?? '—'}</Text>
                                </Table.Td>
                                <Table.Td>
                                  <Text size="xs">{item.hits.toLocaleString('pt-BR')}</Text>
                                </Table.Td>
                              </Table.Tr>
                            ))}
                          </Table.Tbody>
                        </Table>
                      )}
                    </Stack>

                    {/* Top Países */}
                    <Stack gap={4}>
                      <Text size="sm" fw={600}>Top países</Text>
                      {!logs.top_countries || logs.top_countries.length === 0 ? (
                        <Text size="sm" c="dimmed">—</Text>
                      ) : (
                        <Table striped withTableBorder style={{ maxWidth: 300 }}>
                          <Table.Thead>
                            <Table.Tr>
                              <Table.Th>País</Table.Th>
                              <Table.Th style={{ width: 80 }}>Hits</Table.Th>
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            {logs.top_countries.map((item, i) => (
                              // eslint-disable-next-line react/no-array-index-key
                              <Table.Tr key={i}>
                                <Table.Td>
                                  <Text size="xs">{item.key ?? '—'}</Text>
                                </Table.Td>
                                <Table.Td>
                                  <Text size="xs">{item.hits.toLocaleString('pt-BR')}</Text>
                                </Table.Td>
                              </Table.Tr>
                            ))}
                          </Table.Tbody>
                        </Table>
                      )}
                    </Stack>

                    {/* Top IPs */}
                    <Stack gap={4}>
                      <Text size="sm" fw={600}>Top IPs</Text>
                      {!logs.top_ips || logs.top_ips.length === 0 ? (
                        <Text size="sm" c="dimmed">—</Text>
                      ) : (
                        <Table striped withTableBorder style={{ maxWidth: 340 }}>
                          <Table.Thead>
                            <Table.Tr>
                              <Table.Th>IP</Table.Th>
                              <Table.Th style={{ width: 80 }}>Hits</Table.Th>
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            {logs.top_ips.map((item, i) => (
                              // eslint-disable-next-line react/no-array-index-key
                              <Table.Tr key={i}>
                                <Table.Td>
                                  <Text size="xs" ff="monospace">{item.key ?? '—'}</Text>
                                </Table.Td>
                                <Table.Td>
                                  <Text size="xs">{item.hits.toLocaleString('pt-BR')}</Text>
                                </Table.Td>
                              </Table.Tr>
                            ))}
                          </Table.Tbody>
                        </Table>
                      )}
                    </Stack>
                  </Stack>
                )
              })()}

              {!logsQuery.isLoading && logsQuery.isError && (
                <Alert color="yellow">
                  Não foi possível consultar os logs agora.
                </Alert>
              )}
            </Stack>
          </Stack>
        )
      })()}
    </Drawer>
  )
}
